import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { ServiceWidgetsSettingsStore } from './service-widgets-settings.store';
import { routeParams } from '../../../../../shared/shared.global-service';
import {
    SERVICE_SETTINGS_USER_WIDGETS_TYPE_DEFAULT,
    SERVICE_SETTINGS_USER_WIDGETS_TYPE_KEY,
    ServiceSettingsUserWidgetsType,
    UserWidgetSetting,
} from '@libs/types/src/services/types/service-settings-user-widgets.type';
import {
    ServicesDashboardWidgetsEnum,
    serviceWidgetsWithDaysSetting,
    socialServiceDashboardFeatureWidgets,
} from '../../../../../../../../../types/src/services/enum/services-dashboard-widgets.enum';
import { CONTEXT_TYPE_WIDGETS_SETTINGS } from '@libs/types/src/services/types/service-context-type-settings.type';
import { ServiceTypeEnum } from '@libs/types/src/services/enum/service-type.enum';
import { TranslatePipe } from '@ngx-translate/core';
import { featureOn } from '../../../../../../feature-flag';
import { NURSING_ALLOWANCE_EXPERTISE_CODES } from '@libs/types/src/services/utils/nursing-allowance';
import {
    SERVICE_SETTINGS_INDIVIDUAL_PLAN_TYPE_KEY,
    SERVICE_SETTINGS_MAIL_TYPE_KEY,
    SERVICE_SETTINGS_SERVICE_ANNOUNCEMENT_TYPE_KEY,
    SERVICE_SETTINGS_SUGGESTION_TYPE_KEY,
    SERVICE_SETTINGS_TASKS_TYPE_KEY,
} from '@libs/types/src/services/types/common-service-settings.type';
import { applyEach, form, FormField, min } from '@angular/forms/signals';
import { CheckInputComponent } from '../../../../../../components/kit/input/check-input-v2/check-input.component';
import { NumberInputComponent } from '../../../../../../components/kit/input/text-input-v2/number-input.component';
import { GroupObject, ServiceGridItemObject } from '@it2go/types';
import {
    MultiSelectInputComponent,
} from '../../../../../../components/kit/input/multi-select-input-v2/multi-select-input.component';
import { AclService } from '../../../../../../services/acl.service';
import { firstValueFrom } from 'rxjs';
import {
    isServiceDashboardWidgetAvailableForService,
} from '../../../../features/service-dashboard/service-dashboard-widget-policies';

@Component({
    selector: 'app-service-widgets-settings',
    templateUrl: './service-widgets-settings.component.html',
    styleUrl: './service-widgets-settings.component.scss',
    providers: [ServiceWidgetsSettingsStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslatePipe,
        FormField,
        CheckInputComponent,
        NumberInputComponent,
        MultiSelectInputComponent,
    ],
})
export class ServiceWidgetsSettingsComponent {
    protected readonly store = inject(ServiceWidgetsSettingsStore);
    private readonly aclService = inject(AclService);
    private formInitialized = false;
    private groupsOrganizationId: number | null = null;

    protected readonly dashboardSettingsEnabled = computed(() => (
        featureOn.SOCIAL_SERVICE_DASHBOARD && this.store.service()?.type === ServiceTypeEnum.SOCIAL
    ));
    protected readonly groups = signal<GroupObject[]>([]);

    protected readonly formModel = signal<ServiceSettingsUserWidgetsType>({ widgets: [] });
    protected readonly form = form(this.formModel, path => {
        applyEach(path.widgets, widget => {
            min(widget.days, 0);
        });
    });

    constructor() {
        effect(() => {
            const service = this.store.service();
            if (!service || this.formInitialized) return;

            this.formModel.set({ widgets: this.createWidgetSettings(service) });
            this.formInitialized = true;
        });

        effect(() => {
            const service = this.store.service();
            if (!service || !this.dashboardSettingsEnabled()) return;

            const organizationId = service.organizationId
                || this.store.globalGlobalStore.organizationStore.selectedId();
            if (!organizationId || organizationId === this.groupsOrganizationId) return;

            this.groupsOrganizationId = organizationId;
            void firstValueFrom(this.aclService.allAvailableGroups({ organizationId })).then(groups => {
                this.groups.set([...groups].sort((a, b) => a.name.localeCompare(b.name)));
            });
        });
    }

    private createWidgetSettings(service: ServiceGridItemObject): UserWidgetSetting[] {
        const defaultWidgets = SERVICE_SETTINGS_USER_WIDGETS_TYPE_DEFAULT.widgets;
        const storedSetting = service.serviceSettings.find(setting => setting.key === SERVICE_SETTINGS_USER_WIDGETS_TYPE_KEY);
        const storedWidgets = storedSetting
            ? (JSON.parse(storedSetting.data) as ServiceSettingsUserWidgetsType).widgets ?? []
            : [];
        let widgets = storedWidgets.flatMap(widget => {
            const defaultWidget = defaultWidgets.find(item => item.name === widget.name);
            return defaultWidget ? [{
                ...defaultWidget,
                ...widget,
                allowHide: widget.allowHide ?? false,
                visibleForGroupIds: widget.visibleForGroupIds ?? [],
            }] : [];
        });

        // add also widgets that has no stored value in service settings (typically newly added widgets)
        widgets = [
            ...widgets,
            ...defaultWidgets
                .filter(widget => !widgets.some(currentWidget => currentWidget.name === widget.name))
                .map(widget => ({ ...widget })),
        ];

        const availableWidgets = CONTEXT_TYPE_WIDGETS_SETTINGS[service.type as ServiceTypeEnum];
        widgets = widgets.filter(widget => widget.name in availableWidgets);

        if (featureOn.SOCIAL_SERVICE_DASHBOARD && service.type === ServiceTypeEnum.SOCIAL) {
            return widgets.filter(widget => isServiceDashboardWidgetAvailableForService(widget.name, service));
        }

        if (!featureOn.SOCIAL_SERVICE_DASHBOARD) {
            widgets = widgets.filter(widget => !socialServiceDashboardFeatureWidgets.includes(widget.name));
        }

        const useIndividualPlans = service.serviceSettings.some(
            setting => setting.key === SERVICE_SETTINGS_INDIVIDUAL_PLAN_TYPE_KEY && (JSON.parse(setting.data)?.useCard ?? true),
        ) || !service.serviceSettings.some(setting => setting.key === SERVICE_SETTINGS_INDIVIDUAL_PLAN_TYPE_KEY);
        if (service.type === ServiceTypeEnum.SOCIAL && !useIndividualPlans) {
            widgets = widgets.filter(widget => widget.name !== ServicesDashboardWidgetsEnum.INDIVIDUAL_PLAN);
        }

        const useUserTasks = service.serviceSettings.some(
            setting => setting.key === SERVICE_SETTINGS_TASKS_TYPE_KEY && (JSON.parse(setting.data).useCard ?? true),
        );
        if (service.type === ServiceTypeEnum.SOCIAL && !useUserTasks) {
            widgets = widgets.filter(widget => widget.name !== ServicesDashboardWidgetsEnum.USER_TASKS);
        }

        if (service.type == ServiceTypeEnum.HEALTH_CARE && service.expertise) {
            const expertiseCode = service.expertise.code;
            widgets = widgets.filter(widget => {
                return ['902', '913', '925'].includes(expertiseCode) || widget.name !== ServicesDashboardWidgetsEnum.NEAR_END_VOUCHERS;
            });
        }

        const disableMail = service.serviceSettings.filter(setting => setting.key === SERVICE_SETTINGS_MAIL_TYPE_KEY)
            .some((it) => JSON.parse(it.data).useCard === false);
        if (service.type == ServiceTypeEnum.SOCIAL && (!featureOn.SOCIAL_JANUARY || disableMail)) {
            widgets = widgets.filter(widget => widget.name !== ServicesDashboardWidgetsEnum.MAIL_RECORDS);
        }

        const useSuggestion = service.serviceSettings.some(
            setting => setting.key === SERVICE_SETTINGS_SUGGESTION_TYPE_KEY && (JSON.parse(setting.data)?.useCard ?? true),
        );
        if (service.type === ServiceTypeEnum.SOCIAL && (!featureOn.SUGGESTION || !useSuggestion)) {
            widgets = widgets.filter(widget => widget.name !== ServicesDashboardWidgetsEnum.SUGGESTION);
        }

        if (!featureOn.USER_MESSAGES) {
            widgets = widgets.filter(widget => widget.name !== ServicesDashboardWidgetsEnum.USER_MESSAGES);
        }

        const useServiceAnnouncement = service.serviceSettings.some(
            setting => setting.key === SERVICE_SETTINGS_SERVICE_ANNOUNCEMENT_TYPE_KEY && (JSON.parse(setting.data)?.useCard ?? true),
        );
        if (service.type === ServiceTypeEnum.SOCIAL && (!featureOn.SERVICE_ANNOUNCEMENT || !useServiceAnnouncement)) {
            widgets = widgets.filter(widget => widget.name !== ServicesDashboardWidgetsEnum.SERVICE_ANNOUNCEMENTS);
        }
        if (!featureOn.NURSING_ALLOWANCE
            || !NURSING_ALLOWANCE_EXPERTISE_CODES.includes(service.expertise?.code || '')) {
            widgets = widgets.filter((widget: UserWidgetSetting) => ![ServicesDashboardWidgetsEnum.NURSING_ALLOWANCE_DURATION,
                ServicesDashboardWidgetsEnum.NURSING_ALLOWANCE_CONFIRMATION].includes(widget.name));
        }

        return widgets;
    }

    async saveWidgetSettings() {
        const serviceId = routeParams.serviceId();
        this.form().markAsTouched();
        if (!serviceId || this.form().invalid()) {
            return;
        }

        await this.store.api.updateServiceSettings({
            key: SERVICE_SETTINGS_USER_WIDGETS_TYPE_KEY,
            data: JSON.stringify(this.formModel()),
            serviceId: Number(serviceId),
        });
    }

    protected readonly serviceWidgetsWithDaysSetting = serviceWidgetsWithDaysSetting;
}
